import { useState } from 'react';

import { trpc } from '@/lib/api/trpc';

function saveBase64Zip(base64: string, filename: string) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/zip' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Audit pack (PDF + CSVs ZIP) downloads for partner statements.
 * `downloadOne` tracks the in-flight report id so tables can spin per row;
 * `downloadAll` runs sequentially (one tRPC call per statement).
 */
export function useAuditPackDownload(partner: string) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [allProgress, setAllProgress] = useState<{ done: number; total: number } | null>(null);

  const mutation = trpc.partnerReports.downloadAuditPack.useMutation({
    onSuccess: (res) => saveBase64Zip(res.base64, res.filename),
    onSettled: () => setPendingId(null),
  });

  const downloadOne = (reportId: string) => {
    setPendingId(reportId);
    mutation.mutate({ partner, reportId });
  };

  const downloadAll = async (reportIds: string[]) => {
    setAllProgress({ done: 0, total: reportIds.length });
    try {
      for (const reportId of reportIds) {
        const res = await mutation.mutateAsync({ partner, reportId });
        saveBase64Zip(res.base64, res.filename);
        setAllProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
      }
    } finally {
      setAllProgress(null);
    }
  };

  return {
    downloadOne,
    downloadAll,
    pendingId,
    downloadingAll: allProgress !== null,
    allProgress,
  };
}
